import { useEffect, useRef, useState } from 'react';
import frame_1 from '../../../assets/images/selfieFrames/en/frame_1.png';
import frame_2 from '../../../assets/images/selfieFrames/en/frame_2.png';
import frame_3 from '../../../assets/images/selfieFrames/en/frame_3.png';
import frame_4 from '../../../assets/images/selfieFrames/en/frame_4.png';
import frame_5 from '../../../assets/images/selfieFrames/en/frame_5.png';
import frame_6 from '../../../assets/images/selfieFrames/en/frame_6.png';
import frame_7 from '../../../assets/images/selfieFrames/en/frame_7.png';
import frame_1_pl from '../../../assets/images/selfieFrames/pl/frame_1_pl.png';
import frame_2_pl from '../../../assets/images/selfieFrames/pl/frame_2_pl.png';
import frame_3_pl from '../../../assets/images/selfieFrames/pl/frame_3_pl.png';
import frame_4_pl from '../../../assets/images/selfieFrames/pl/frame_4_pl.png';
import { useBoundingRect } from '@/util/useBoundingRect.ts';
import { useResponsiveCanvas } from '@/components/slides/Selfie/useResponsiveCanvas.ts';
import { useVideoSize } from '@/util/useVideoSize.ts';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { isIosSafari } from '@/util/isIosSafari.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';

/*
  canvasDrawScaledFrame:

  Takes a Canvas painting 2d Context and the Imported source of an image.

  Creates a new Image to draw onto the Canvas, sourced by the "frameSrc" parameter.
  Calculations the width/height ration and scales + centers the image on the canvas

 */
const canvasDrawScaledFrame = (ctx: CanvasRenderingContext2D, frameSrc: any) => {
  const frameImg = new Image();
  frameImg.onload = () => {
    const canvas = ctx.canvas;
    const hRatio = canvas.width / frameImg.width;
    const vRatio = canvas.height / frameImg.height;
    const ratio = Math.min(hRatio, vRatio);
    const centerShift_x = (canvas.width - frameImg.width * ratio) / 2;
    const centerShift_y = (canvas.height - frameImg.height * ratio) / 2;

    ctx.drawImage(
      frameImg,
      0,
      0,
      frameImg.width,
      frameImg.height,
      centerShift_x,
      centerShift_y,
      frameImg.width * ratio,
      frameImg.height * ratio
    );
  };
  frameImg.src = frameSrc;
};

const clearCanvas = (ctx: CanvasRenderingContext2D) => {
  const canvas = ctx.canvas;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
};

const framesByLocale: Record<string, string[]> = {
  DE: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  GB: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  CL: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  IN: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  US: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  ZA: [frame_1, frame_2, frame_3, frame_4, frame_5, frame_6, frame_7],
  PL: [frame_1_pl, frame_2_pl, frame_3_pl, frame_4_pl],
};

export const useSelfieCanvas = () => {
  const [parentRef, parentRect] = useBoundingRect();
  const [videoRef, videoSize] = useVideoSize();
  const { showLayer } = useLayer();

  const responsive = useResponsiveCanvas(parentRect, videoSize);

  const mainCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoStreamRef = useRef<MediaStream>(null);
  const frameIndex = useRef(0);
  const snapshotCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hasSnapshot, setHasSnapshot] = useState<boolean>(false);
  const facingMode = useRef<'user' | 'environment'>('user');
  const { currentSlide } = useNavigation();
  const { eventConfig } = useEventConfig();
  const [notAllowed, setNotAllowed] = useState<boolean>(false);
  const [shareButtonWasClicked, setShareButtonWasClicked] = useState<boolean>(false);

  const [nextFrameSrc, setNextFrameSrc] = useState(frame_2);
  const [prevFrameSrc, setPrevFrameSrc] = useState(frame_4);

  const locale = eventConfig?.meta.country ?? 'DE';

  useEffect(() => {
    setTimeout(init, 100);
    return teardown;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  // Init Fn creates a Temporary Canvas, that stores the Video-Screenshot-Image
  // Then It paints the currently selected "Frame" border image on the Main Canvas
  // Then it starts the Videostream of the camera, that fills the <video> tag
  // (which in turn is positioned BELOW the frame-canvas so it looks like a preview
  const init = () => {
    createTempCanvas();
    drawCurrentFrame();
    startVideoStream();
  };

  // When leaving the overlay/slide we stop the camera, which is important
  // to build trust (and save resources etc)
  const teardown = () => {
    videoStreamRef.current?.getTracks().map((track) => {
      track.stop();
    });
  };

  // Whenever videoSize or parentRect change values, i.e. when the screen
  // is resized or flipped to landscape mode, we drop the existing video-screenshot
  useEffect(() => {
    clearSnapshot();
  }, [videoSize, parentRect]);

  // Data structure on a temporary <canvas> element where we paint the current
  // "screenshot" (snapshot) of the video for preview. From there it gets
  // drawn onto the main-canvas (immediately)
  const createTempCanvas = () => {
    snapshotCanvasRef.current = document.createElement('canvas');
    snapshotCanvasRef.current!.width = 10000;
    snapshotCanvasRef.current!.height = 10000;
  };

  const getWebcamStream = () => {
    return navigator.mediaDevices.getUserMedia({ video: { facingMode: facingMode.current } });
  };

  const startVideoStream = () => {
    if (!videoRef.current) {
      return;
    }
    getWebcamStream()
      .then((stream) => {
        videoStreamRef.current = stream;
        videoRef.current!.srcObject = stream;
        videoRef.current!.onloadedmetadata = () => {
          void videoRef.current!.play();
        };
        setNotAllowed(false);
      })
      .catch((e) => {
        console.log(e);
        setNotAllowed(true);
        showLayer({ ref: '@cameraPermissionsMissing' });
      });
  };

  const getMainContext = (): CanvasRenderingContext2D | null => {
    if (mainCanvasRef.current === null) {
      return null;
    }
    return mainCanvasRef.current!.getContext('2d');
  };

  const getSnapshotContext = (): CanvasRenderingContext2D | null => {
    if (snapshotCanvasRef.current === null) {
      return null;
    }
    return snapshotCanvasRef.current!.getContext('2d');
  };

  // Load the Image of a DKMS frame and paint it onto the main <canvas>
  const drawCurrentFrame = () => {
    const ctx = getMainContext();
    if (!ctx) return;

    const frames = framesByLocale[locale];
    if (!frames || frames.length === 0) return;

    const frameCount = frames.length;
    const currentFrame = frames[frameIndex.current % frameCount];

    canvasDrawScaledFrame(ctx, currentFrame);

    const nextIndex = (frameIndex.current + 1) % frameCount;
    const prevIndex = (frameIndex.current - 1 + frameCount) % frameCount;

    setNextFrameSrc(frames[nextIndex]);
    setPrevFrameSrc(frames[prevIndex]);
  };

  const getFrameCount = (): number => {
    const frames = framesByLocale[locale];
    if (!frames || frames.length === 0) return 0;

    return frames.length;
  };

  const selectNextFrame = () => {
    frameIndex.current = (frameIndex.current + 1) % getFrameCount();
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearCanvas(mainCtx);
    paintSnapshotOnMain();
    drawCurrentFrame();
  };

  const selectPrevFrame = () => {
    const frameCount = getFrameCount();

    frameIndex.current = (frameIndex.current - 1 + frameCount) % frameCount;
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearCanvas(mainCtx);
    paintSnapshotOnMain();
    drawCurrentFrame();
  };

  // When
  const paintSnapshotOnMain = () => {
    const mainCtx = getMainContext();
    const snapCtx = getSnapshotContext();
    if (!mainCtx || !snapCtx) {
      return;
    }

    const storageCanvas = snapCtx.canvas;
    mainCtx.drawImage(snapshotCanvasRef.current!, 0, 0, storageCanvas.width, storageCanvas.height);
  };

  // Storing the snapshot means taking a still-frame from the <video>
  // tag and painting it on the temporary <canvas>. The Snapshot needs
  // to be positioned in the same way that the responsiveness on the "preview"
  // works. The <video> tag in the overlay is moved to the top and the left
  // in order to center it below the frame. (video might be way bigger than the "frame")
  const storeSnapshotImageNormal = () => {
    const snapCtx = getSnapshotContext();
    const video = videoRef.current;
    if (!snapCtx || !video) {
      return;
    }

    const videoWidth = video?.videoWidth || 100;
    const videoHeight = video?.videoHeight || 100;
    const { width, height } = mainCanvasRef.current!.getBoundingClientRect();
    const leftOffset = (width - videoWidth) / 2;
    const topOffset = (height - videoHeight) / 2;

    // Video is flipped Horizinatally, we need to do this for the canvas
    snapCtx.translate(width, 0);
    snapCtx.scale(-1, 1);
    snapCtx.drawImage(video, leftOffset, topOffset, videoWidth, videoHeight);
    // Unflip the canvas so a possible "next try" will not be reversed
    snapCtx.translate(width, 0);
    snapCtx.scale(-1, 1);
  };

  const storeSnapshotImage = () => {
    if (!isIosSafari()) {
      storeSnapshotImageNormal();
    }
  };

  const clearSnapshotImage = () => {
    const snapCtx = getSnapshotContext();
    if (!snapCtx) {
      return;
    }
    const canvas = snapCtx.canvas;
    snapCtx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const takeSnapshot = () => {
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearCanvas(mainCtx);
    storeSnapshotImage();
    paintSnapshotOnMain();
    drawCurrentFrame();
    setHasSnapshot(true);
  };

  const clearSnapshot = () => {
    const mainCtx = getMainContext();
    if (!mainCtx) {
      return;
    }
    clearSnapshotImage();
    clearCanvas(mainCtx);
    drawCurrentFrame();
    setHasSnapshot(false);
  };

  // The share menu. Grab the image from main <canvas>
  // convert it to a File and send it to the navigator.share native menu where
  // the user can now select InstaChat or Twix or whatever she has installed on
  // their phone. Suggestions for title/url are taken from the slide.yaml config
  const shareSnapshot = () => {
    setShareButtonWasClicked(true);
    mainCanvasRef.current!.toBlob((blob) => {
      if (!blob) {
        throw 'Something went wrong with the blob.';
        return;
      }
      const file = new File([blob], 'dkms.png', {
        type: blob.type,
        lastModified: new Date().getTime(),
      });

      const shareData: ShareData = {
        title: (currentSlide?.selfiesharetitle as string) || 'DKMS',
        text: (currentSlide?.selfiesharetext as string) || 'Saving lives!',
        url: (currentSlide?.selfieshareurl as string) || 'https://dkms.de/',
        files: [file],
      };

      try {
        const canShare = navigator.canShare(shareData);
        if (canShare) {
          navigator.share(shareData).catch((e) => {
            if (e.toString().includes('AbortError')) {
              // Normal "cancel" event. Nothing to see here
            } else {
              console.log(e);
            }
          });
        } else {
          showLayer({ ref: '@sharingNotSupported' });
        }
      } catch (_e) {
        showLayer({ ref: '@sharingNotSupported' });
      }
    });
  };

  // Switch between normal and selfie cam.
  const switchCamera = () => {
    if (facingMode.current === 'user') {
      facingMode.current = 'environment';
    } else {
      facingMode.current = 'user';
    }

    startVideoStream();
  };

  return {
    canvasRef: mainCanvasRef,
    parentRef,
    videoRef,
    nextFrame: selectNextFrame,
    prevFrame: selectPrevFrame,
    takeSnapshot,
    clearSnapshot,
    shareSnapshot,
    hasSnapshot,
    switchCamera,
    width: responsive.width,
    height: responsive.height,
    videoOffset: responsive.videoOffset,
    notAllowed,
    restartCamera: init,
    shareButtonWasClicked,
    nextFrameSrc,
    prevFrameSrc,
  };
};
